/**
 * Time format utilities — supports user preference for 12-hour (standard) or 24-hour (military).
 *
 * Usage:
 *   import { formatTime, useTimeFormat } from '../utils/timeFormat';
 *   formatTime(dateObj, '24h') -> '14:00'
 *   formatTime(dateObj, '12h') -> '2:00 PM'
 *
 *   // In components:
 *   const fmt = useTimeFormat();
 *   fmt(dateObj) // uses the current user's preferred format
 */
import { format, parseISO } from 'date-fns';
import { useAuth } from '../context/AuthContext';

export const TIME_FORMATS = {
  '12h': { value: '12h', label: 'Standard (2:00 PM)', pattern: 'h:mm a' },
  '24h': { value: '24h', label: 'Military (14:00)', pattern: 'HH:mm' },
};

export const DEFAULT_TIME_FORMAT = '12h';

/**
 * Format a Date or ISO string into the user's preferred time format.
 *
 * @param {Date|string} input - Date object or ISO string
 * @param {'12h'|'24h'} preference - User's preferred format
 * @returns {string} Formatted time string (empty string if input is falsy)
 */
export const formatTime = (input, preference = DEFAULT_TIME_FORMAT) => {
  if (!input) return '';
  const date = typeof input === 'string' ? parseISO(input) : input;
  if (Number.isNaN(date?.getTime?.())) return '';
  const pattern = (TIME_FORMATS[preference] || TIME_FORMATS[DEFAULT_TIME_FORMAT]).pattern;
  return format(date, pattern);
};

/**
 * Convert a stored local-time string (e.g., "12:46 PM" or "14:00") into the user's preferred format.
 * Returns the original string if it can't be parsed.
 */
export const reformatLocalTime = (localTimeStr, preference = DEFAULT_TIME_FORMAT) => {
  if (!localTimeStr) return '';
  // Already a 24h string like "14:00" - convert if preference is 12h
  const military = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(localTimeStr.trim());
  if (military) {
    const h = parseInt(military[1], 10);
    const m = parseInt(military[2], 10);
    if (preference === '24h') return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  }
  // 12h string like "2:00 PM" - convert if preference is 24h
  const standard = /^(1[0-2]|0?[1-9]):([0-5]\d)\s*(AM|PM|am|pm)$/.exec(localTimeStr.trim());
  if (standard) {
    let h = parseInt(standard[1], 10);
    const m = parseInt(standard[2], 10);
    const isPM = /pm/i.test(standard[3]);
    if (preference === '12h') {
      return `${h}:${String(m).padStart(2, '0')} ${isPM ? 'PM' : 'AM'}`;
    }
    if (h === 12) h = 0;
    if (isPM) h += 12;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  return localTimeStr;
};

/**
 * React hook returning a memoized formatter bound to the current user's time preference.
 */
export const useTimeFormat = () => {
  const { user } = useAuth();
  const preference = user?.time_format || DEFAULT_TIME_FORMAT;
  const fmt = (input) => formatTime(input, preference);
  fmt.local = (str) => reformatLocalTime(str, preference);
  fmt.preference = preference;
  return fmt;
};
